import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Body1, Body5, Body7, Container } from '@ui';
import { CopyButton, FlatButton, SkeletonButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { ROUTES } from '@constants/routes';
import { REVIEW_ACTIONS, decideExtractionRows, fetchExtractionRow, fetchSourceCells } from '@services/fmdc';
import { Chip } from '@features/scoreSpec/styles';
import { DefList } from './DefList';
import { StatusPill } from './StatusPill';
import { ConfidenceDot } from './ConfidenceDot';
import { CorrectForm } from './CorrectForm';
import { SourceGrid, widenWindow } from './SourceGrid';
import { HistoryList } from './HistoryList';
import { fmdcPaths } from './fmdcUrl';
import {
  EM_DASH,
  formatLoadAction,
  formatReviewerStatus,
  formatValidationStatus,
  formatValue,
  humanizeToken,
} from './format';
import { REVIEWER_STATUS_TOKENS, VALIDATION_TONES, tokenFor } from './statusTokens';
import {
  DangerTransparentButton,
  HeaderCell,
  HeaderRow,
  Mono,
  Row,
  SectionHead,
  Table,
  TableScroll,
  TwoColumn,
  rowKeyHandler,
} from './styles';

// One extracted cell in context: what it is, where it came from, what else is the same item, and
// the form to correct its classification. GET /extraction_rows/:id, then the source window it
// names through GET /runs/:id/source_cells.

const NATURAL_KEY_PREVIEW = 60;

// Period | Scenario | Value | Cell | Status
const SAME_ITEM_COLUMNS =
  'minmax(80px, 1fr) minmax(80px, 1fr) minmax(100px, 1.2fr) minmax(120px, 1.4fr) minmax(90px, 1fr)';

const first = (...values) => values.find((v) => v !== undefined && v !== null && v !== '');

// The row's fields, read defensively: GET /extraction_rows/:id may carry the payload shape (nested
// period/split/units/source_location objects) or the Extraction_Values columns (flat).
export const describeRow = (row, catalogItem) => {
  if (!row) return null;
  const period = row.period && typeof row.period === 'object' ? row.period : null;
  const split = row.split && typeof row.split === 'object' ? row.split : null;
  const units = row.units && typeof row.units === 'object' ? row.units : null;
  const sign = row.sign && typeof row.sign === 'object' ? row.sign : null;
  const source = row.source_location && typeof row.source_location === 'object' ? row.source_location : null;
  const periodLabel = first(period?.label, row.fiscal_period_label, row.period_label);
  const periodEnd = first(period?.end_date, row.period_end_date);
  const periodType = first(period?.type, row.period_type);
  const splitType = first(split?.type, row.split_type);
  const splitLabel = first(split?.label, row.split_label);
  return {
    label: first(row.item_label, catalogItem?.canonical_item_label, row.canonical_item_code, row.item) || EM_DASH,
    code: first(row.canonical_item_code, row.item) || EM_DASH,
    section: [row.section, row.sub_section].filter(Boolean).map(humanizeToken).join(' / ') || null,
    split: splitType ? `${humanizeToken(splitType)}${splitLabel ? `: ${splitLabel}` : ''}` : splitLabel || null,
    metricComponent: row.metric_component ? humanizeToken(row.metric_component) : null,
    period: periodLabel
      ? `${periodLabel}${periodEnd ? ` (${periodEnd}${periodType ? `, ${periodType}` : ''})` : ''}`
      : periodEnd || null,
    scenario: row.scenario ? humanizeToken(row.scenario) : null,
    series: [row.series, row.model_case].filter(Boolean).map(humanizeToken).join(' / ') || null,
    value: formatValue(first(row.value, row.normalized_value) ?? null, {
      valueKind: row.value_kind,
      asReported: row.value_as_reported,
    }),
    asReported: row.value_as_reported,
    units: first(units?.normalized, typeof row.units === 'string' ? row.units : null),
    sign: first(sign?.convention, row.sign_convention),
    loadAction: row.load_action,
    confidence: row.confidence,
    validationStatus: row.validation_status,
    warningCodes: Array.isArray(row.warning_codes) ? row.warning_codes : [],
    reviewerStatus: row.reviewer_status,
    naturalKey: row.natural_key,
    sourceCell: first(source?.cell, row.source_cell, row.cell),
    rawLabel: first(row.raw_label, row.source_row_label_raw),
  };
};

const toWindowRequest = (window) =>
  window
    ? {
        sheet: window.sheet,
        rowFrom: window.row_from ?? window.rowFrom,
        rowTo: window.row_to ?? window.rowTo,
        colFrom: window.col_from ?? window.colFrom,
        colTo: window.col_to ?? window.colTo,
      }
    : null;

export const ValueDetailView = ({ runId, rowId }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [detail, setDetail] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [sourceWindow, setSourceWindow] = React.useState(null);
  const [sourceError, setSourceError] = React.useState(null);
  const [sourceLoading, setSourceLoading] = React.useState(false);
  const [deciding, setDeciding] = React.useState(null);
  const [decideError, setDecideError] = React.useState(null);

  const loadSource = React.useCallback(
    async (request, state) => {
      if (!request || !request.sheet) return;
      setSourceLoading(true);
      setSourceError(null);
      const result = await fetchSourceCells(runId, request);
      if (state?.isCanceled) return;
      setSourceLoading(false);
      if (result.error) setSourceError(result.error);
      else setSourceWindow(result.window);
    },
    [runId]
  );

  const load = React.useCallback(
    async (state) => {
      setError(null);
      const result = await fetchExtractionRow(rowId);
      if (state?.isCanceled) return;
      setIsLoading(false);
      if (result.error) {
        setError(result.error);
        return;
      }
      setDetail(result.detail);
      loadSource(toWindowRequest(result.detail?.source_window), state);
    },
    [rowId, loadSource]
  );

  React.useEffect(() => {
    const state = { isCanceled: false };
    setIsLoading(true);
    load(state);
    return () => {
      state.isCanceled = true;
    };
  }, [load]);

  const row = detail?.row;
  const described = React.useMemo(() => describeRow(row, detail?.catalog_item), [row, detail]);

  // After a decide only the reviewer status and the history move. The source window the reviewer
  // may have widened, and the correction they may be composing, stay as they are.
  const refreshDetail = React.useCallback(async () => {
    const result = await fetchExtractionRow(rowId);
    if (!result.error) setDetail(result.detail);
  }, [rowId]);

  const decide = async (action) => {
    if (deciding || !row) return;
    setDeciding(action);
    setDecideError(null);
    const result = await decideExtractionRows({ ids: [row.id], action });
    setDeciding(null);
    if (result.error) {
      setDecideError(result.error);
      return;
    }
    refreshDetail();
  };

  const handleCorrected = (result) => {
    if (result?.row) setDetail((prev) => ({ ...prev, row: { ...prev?.row, ...result.row } }));
    else load();
  };

  const openSameItem = (item) => {
    if (!item?.id || String(item.id) === String(rowId)) return;
    navigate(fmdcPaths.value(ROUTES.fmdc, runId, item.id), { state: location.state });
  };

  const widen = () => {
    const next = widenWindow(sourceWindow);
    if (next) loadSource(next);
  };

  if (isLoading) {
    return <Body5 isLowContrast>Loading value…</Body5>;
  }

  if (error || !row) {
    return (
      <div>
        <Body5>{error || 'This value could not be read.'}</Body5>
        <Container top={12}>
          <SkeletonButton onClick={() => load()}>Retry</SkeletonButton>
        </Container>
      </div>
    );
  }

  const validation = detail.validation || [];
  const sameItem = detail.same_item || [];
  const naturalKeyPreview =
    described.naturalKey && described.naturalKey.length > NATURAL_KEY_PREVIEW
      ? `${described.naturalKey.slice(0, NATURAL_KEY_PREVIEW)}…`
      : described.naturalKey;

  const identityRows = [
    { label: 'Section', value: described.section },
    { label: 'Split', value: described.split },
    { label: 'Metric component', value: described.metricComponent },
    { label: 'Period', value: described.period },
    { label: 'Scenario', value: described.scenario },
    { label: 'Series / case', value: described.series },
    { label: 'Value', value: described.value },
    {
      label: 'As reported',
      value:
        described.asReported !== undefined && described.asReported !== null ? (
          <Mono>{String(described.asReported)}</Mono>
        ) : null,
    },
    { label: 'Units', value: described.units },
    { label: 'Sign', value: described.sign ? humanizeToken(described.sign) : null },
    {
      label: 'Load action',
      value: described.loadAction ? (
        <StatusPill token="secondaryText" label={formatLoadAction(described.loadAction)} />
      ) : null,
    },
    { label: 'Confidence', value: described.confidence ? <ConfidenceDot confidence={described.confidence} /> : null },
    {
      label: 'Validation',
      value: described.validationStatus ? (
        <Chip $tone={VALIDATION_TONES[described.validationStatus] || 'neutral'}>
          {formatValidationStatus(described.validationStatus)}
          {described.warningCodes.length ? ` · ${described.warningCodes.join(', ')}` : ''}
        </Chip>
      ) : null,
    },
    {
      label: 'Reviewer status',
      value: described.reviewerStatus ? (
        <StatusPill
          token={tokenFor(REVIEWER_STATUS_TOKENS, described.reviewerStatus)}
          label={formatReviewerStatus(described.reviewerStatus)}
        />
      ) : null,
    },
    { label: 'Source cell', value: described.sourceCell ? <Mono>{described.sourceCell}</Mono> : null },
    { label: 'Row label', value: described.rawLabel ? <Mono>{String(described.rawLabel).trim()}</Mono> : null },
    {
      label: 'Natural key',
      value: described.naturalKey ? (
        <Container row verticallyCenter gap={8} flexWrap>
          <Mono title={described.naturalKey}>{naturalKeyPreview}</Mono>
          <CopyButton text={described.naturalKey} transparent />
        </Container>
      ) : null,
    },
  ];

  return (
    <div>
      <TwoColumn>
        <div>
          <Body1 bold>{described.label}</Body1>
          <Container top={4}>
            <Mono>{described.code}</Mono>
          </Container>
          <Container top={16}>
            <DefList rows={identityRows} />
          </Container>

          <Container top={24}>
            <SectionHead heading="Validation" subtext="Rules that looked at this value" />
            <Container top={12}>
              {validation.length === 0 ? (
                <Body5 isLowContrast>No rules flagged.</Body5>
              ) : (
                validation.map((entry) => (
                  <Container key={`${entry.rule_id}-${entry.message}`} bottom={8} row verticallyCenter gap={8} flexWrap>
                    <Chip $tone={VALIDATION_TONES[entry.status] || 'neutral'}>
                      {entry.rule_id}
                      {entry.severity ? ` · ${entry.severity}` : ''}
                    </Chip>
                    <Body5>{entry.message}</Body5>
                  </Container>
                ))
              )}
            </Container>
          </Container>

          <Container top={24}>
            <SectionHead heading="Same item" subtext="The other cells of this item on this run" />
            <Container top={12}>
              {sameItem.length === 0 ? (
                <Body5 isLowContrast>No other cells of this item.</Body5>
              ) : (
                <TableScroll>
                  <Table $minWidth={520}>
                    <HeaderRow $columns={SAME_ITEM_COLUMNS}>
                      <HeaderCell>Period</HeaderCell>
                      <HeaderCell>Scenario</HeaderCell>
                      <HeaderCell>Value</HeaderCell>
                      <HeaderCell>Cell</HeaderCell>
                      <HeaderCell>Status</HeaderCell>
                    </HeaderRow>
                    {sameItem.map((item) => {
                      const isCurrent = String(item.id) === String(rowId);
                      return (
                        <Row
                          key={item.id}
                          $columns={SAME_ITEM_COLUMNS}
                          $interactive={!isCurrent}
                          $selected={isCurrent}
                          role={isCurrent ? undefined : 'button'}
                          tabIndex={isCurrent ? undefined : 0}
                          aria-current={isCurrent ? 'true' : undefined}
                          aria-label={
                            isCurrent ? undefined : `Open ${item.period || 'cell'} ${item.scenario || ''}`.trim()
                          }
                          onClick={() => openSameItem(item)}
                          onKeyDown={isCurrent ? undefined : rowKeyHandler(() => openSameItem(item))}
                        >
                          <Body7 bold={isCurrent}>{item.period || EM_DASH}</Body7>
                          <Body7 isLowContrast>{item.scenario ? humanizeToken(item.scenario) : EM_DASH}</Body7>
                          <Body7>{formatValue(item.value, { valueKind: row.value_kind })}</Body7>
                          <Body7 isLowContrast>{[item.sheet, item.cell].filter(Boolean).join('!') || EM_DASH}</Body7>
                          <Body7 isLowContrast>{formatReviewerStatus(item.reviewer_status)}</Body7>
                        </Row>
                      );
                    })}
                  </Table>
                </TableScroll>
              )}
            </Container>
          </Container>

          <Container top={24}>
            <SectionHead heading="Decide this cell" subtext="This cell only; not saved for the issuer." />
            <Container top={12} row verticallyCenter gap={12} flexWrap>
              <FlatButton onClick={() => decide(REVIEW_ACTIONS.APPROVE)} disabled={!!deciding}>
                {deciding === REVIEW_ACTIONS.APPROVE ? 'Approving…' : 'Approve'}
              </FlatButton>
              <DangerTransparentButton onClick={() => decide(REVIEW_ACTIONS.REJECT)} disabled={!!deciding}>
                {deciding === REVIEW_ACTIONS.REJECT ? 'Rejecting…' : 'Reject'}
              </DangerTransparentButton>
              {decideError && <ErrorText>{decideError}</ErrorText>}
            </Container>
          </Container>

          <Container top={24}>
            <CorrectForm row={row} catalogItem={detail.catalog_item} onCorrected={handleCorrected} />
          </Container>
        </div>

        <div>
          <SourceGrid
            window={sourceWindow}
            focusCell={described.sourceCell}
            onWiden={widen}
            isLoading={sourceLoading}
            error={sourceError}
            onRetry={() => loadSource(toWindowRequest(detail.source_window))}
          />
        </div>
      </TwoColumn>

      <Container top={24}>
        <HistoryList events={detail.history || []} />
      </Container>
    </div>
  );
};
